import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { CalendarX2, ArrowLeft, Receipt } from "lucide-react";
import { fmtDateTime } from "./dateFmt";

const CANCELLED_BY_ROLE = {
  staff: "team member",
  customer: "guest · self-service portal",
  system: "system",
};

// "Cancelled by" must name the actual person. Legacy rows written before the
// username was stamped fall back to the role word alone.
function cancelledByText(appt) {
  const role = CANCELLED_BY_ROLE[appt.cancelled_by] || appt.cancelled_by || "—";
  const name = (appt.cancelled_by_name || "").trim();
  if (!name) return `${role} (name not recorded on this older booking)`;
  return `${name} · ${role}`;
}

const Row = ({ label, value, testid }) => (
  <div className="flex gap-3 py-1.5 border-b border-border/60 last:border-0">
    <span className="w-44 shrink-0 text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="text-sm text-foreground" data-testid={testid}>{value}</span>
  </div>
);

/**
 * Read-only view for a CANCELLED / NO-SHOW booking.
 *
 * A cancelled visit never happened, so it must never seed a fresh invoice.
 * Opening the eye icon on such a booking used to POST a brand-new unpaid
 * order, polluting every revenue report. This panel is what the operator
 * gets instead: the cancellation audit trail, plus a direct link to the
 * original invoice when one exists.
 */
export default function CancelledBookingPanel({ appt, branchId, order, onBack }) {
  if (!appt) return null;
  const isNoShow = (appt.status || "").toLowerCase() === "no_show";
  const reason = appt.cancellation_reason_label || "(no reason recorded)";
  const note = appt.cancellation_note || "";
  const by = cancelledByText(appt);
  const systemWritten = !!appt.system_cancelled;

  return (
    <Card className="rounded-lg" data-testid="cancelled-booking-panel">
      <CardContent className="p-0">
        <div className="px-4 py-3 bg-rose-50 border-b border-rose-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-rose-900">
            <CalendarX2 className="w-4 h-4" />
            <span className="text-sm font-medium">
              {isNoShow ? "No-show booking" : "Cancelled booking"} · no invoice was created
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={onBack} data-testid="cancelled-booking-back">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to orders
          </Button>
        </div>
        <div className="p-4">
          <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
            This visit never took place, so the system will not open a checkout or create a new order
            for it. Below is the cancellation record exactly as it was written.
          </p>
          <Row label="Booking #" testid="cancelled-booking-number" value={<span className="font-mono">{String(appt.id).slice(0, 8).toUpperCase()}</span>} />
          <Row label="Status" testid="cancelled-booking-status" value={
            <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-800">
              {isNoShow ? "NO SHOW" : "CANCELLED"}
            </Badge>
          } />
          <Row label="Guest" testid="cancelled-booking-guest" value={appt.customer_name || "Walk-in"} />
          <Row label="Service" testid="cancelled-booking-service" value={appt.service_name || "—"} />
          <Row label="Appointment date" testid="cancelled-booking-appt-date" value={fmtDateTime(appt.start_iso)} />
          <Row label="Cancelled at" testid="cancelled-booking-cancelled-at" value={appt.cancelled_at ? fmtDateTime(appt.cancelled_at) : "—"} />
          <Row label="Cancelled by" testid="cancelled-booking-cancelled-by" value={by} />
          <Row label="Reason" testid="cancelled-booking-reason" value={reason} />
          {note ? <Row label="Note" testid="cancelled-booking-note" value={note} /> : (
            <Row label="Note" testid="cancelled-booking-note" value={<span className="text-muted-foreground">No note was written.</span>} />
          )}
          <Row
            label="Source"
            testid="cancelled-booking-source"
            value={systemWritten
              ? "System write (service line removed while editing the visit / invoice) — excluded from cancellation reports"
              : "Cancel Appointment action — counted in cancellation reports"}
          />
          <Row
            label="Invoice"
            testid="cancelled-booking-invoice"
            value={order
              ? (
                <Link
                  to={`/staff/branch/${branchId}/orders?order=${order.id}`}
                  className="inline-flex items-center gap-1 text-primary hover:underline font-mono"
                >
                  <Receipt className="w-3.5 h-3.5" /> {order.order_number || order.id.slice(0, 8)}
                </Link>
              )
              : "No invoice covers this booking."}
          />
        </div>
      </CardContent>
    </Card>
  );
}
